import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select, { components, GroupBase, OptionProps, ControlProps, MultiValueProps, StylesConfig, SelectInstance } from "react-select";

import CloseRoundedIcon from "@assets/icons/close-icon-rounded.svg";

import { APP_COLORS } from "@styles/colors";
import { CompanyAvatar, CompanyAvatarSizes } from "@ui/Avatars";
import { Checkbox } from "@ui/FormElements";
import { cn } from "@utils/tailwind.util";
import useSystemStore from "@store/useSystemStore";
import { DropdownIndicator } from "@ui/Select/Select";
import { usePersonAndUserOptions } from "@components/AddNewEntity/hooks/usePersonAndUserOptions";
import { IPeople } from "@interfaces/people.interface";
import { getFullNameOrEmail } from "@utils/tags.util";

export type PersonValueItem = Partial<IPeople & {
  label?: string;
  logo_url?: string;
  avatar_url?: string;
  isUser?: boolean;
  title?: { id: string; title: string; order?: number } | string | null;
}>;

interface PersonOption {
  id?: string;
  value: string;
  label: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  logo_url?: string;
  primary_company?: PersonValueItem["primary_company"];
  isTeamMember?: boolean;
  isUser?: boolean;
  title?: PersonValueItem["title"];
}

interface ISourcePeopleSelectProps {
  value: {
    person: PersonValueItem[];
    user: PersonValueItem[];
  };
  onChange: (data: { person: PersonValueItem[]; user: PersonValueItem[] }) => void;
  placeholder?: string;
  companyId?: string | null;
}

const getItemId = (item: PersonValueItem): string => item.id || "";
const getItemLabel = (item: PersonValueItem): string =>
  item.label || getFullNameOrEmail(item);

const CustomOption = ({
  optionProps,
}: {
  optionProps: OptionProps<PersonOption, true, GroupBase<PersonOption>>;
}) => {
  const { isDarkMode } = useSystemStore();
  const data = optionProps.data;
  const isTeamMember = data.isTeamMember || data.isUser;
  const primaryCompany = data.primary_company?.company;
  const avatarUrl = primaryCompany?.logo_url || data.avatar_url || data.logo_url || "";
  const companyName = primaryCompany?.title;
  const personTitle = data.primary_company?.relation;

  const titleValue = isTeamMember && data.title
    ? typeof data.title === "object" ? data.title.title : data.title
    : null;

  const textColor = isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary;
  const secondaryTextColor = isDarkMode ? APP_COLORS.gray["400"] : "#6B7080";

  return (
    <components.Option {...optionProps}>
      <div
        className="flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          optionProps.selectOption(optionProps.data);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          parentClassName="flex-shrink-0"
          className={cn("cursor-pointer border-stroke-primary bg-transparent", {
            "!bg-teal-600 !border-teal-600 !text-white": optionProps.isSelected,
          })}
          checked={optionProps.isSelected}
          variant="small"
          readOnly
        />

        <CompanyAvatar
          size={CompanyAvatarSizes.BIG}
          logoUrl={avatarUrl}
          userProps={{
            first_name: data.first_name ?? "",
            last_name: data.last_name ?? "",
          }}
        />

        <div className="flex flex-1 flex-col min-w-0">
          {isTeamMember ? (
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-medium truncate" style={{ color: textColor }}>{optionProps.label}</span>
              {titleValue && (
                <span className="text-xs flex-shrink-0" style={{ color: secondaryTextColor }}>
                  {titleValue}
                </span>
              )}
            </div>
          ) : (
            <>
              <span className="text-sm font-medium truncate" style={{ color: textColor }}>{optionProps.label}</span>
              {companyName && (
                <span className="text-xs truncate" style={{ color: secondaryTextColor }}>{companyName}</span>
              )}
              {personTitle && (
                <span className="text-xs truncate" style={{ color: secondaryTextColor }}>{personTitle}</span>
              )}
              {data.email && (
                <span className="text-xs text-teal-600 dark:text-teal-400 underline truncate">{data.email}</span>
              )}
            </>
          )}
        </div>
      </div>
    </components.Option>
  );
};

const CustomControl = ({
  children,
  ...props
}: ControlProps<PersonOption, true, GroupBase<PersonOption>>) => {
  return <components.Control {...props}>{children}</components.Control>;
};

const CustomGroupHeading = (props: any) => {
  const { isDarkMode } = useSystemStore();
  return (
    <div
      className={cn(
        "sticky top-[29px] z-10 border-b px-2 py-2 text-xs uppercase",
        isDarkMode
          ? "border-gray-700 bg-gray-850 text-gray-400"
          : "border-gray-200 bg-white text-gray-500"
      )}
    >
      {props.data.label}
    </div>
  );
};

const CustomClearIndicator = ({ clearValue, hasValue }: { clearValue: () => void; hasValue: boolean }) => {
  if (!hasValue) return null;

  return (
    <div
      className="mr-1 cursor-pointer opacity-70 duration-200 hover:opacity-100"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        clearValue();
      }}
    >
      <CloseRoundedIcon width={16} height={16} color="#6B7080" />
    </div>
  );
};

const getStyles = (isDarkMode: boolean, menuMaxHeight: number): StylesConfig<PersonOption, true> => ({
  control: (provided, state) => ({
    ...provided,
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: state.menuIsOpen
      ? isDarkMode ? APP_COLORS.stroke.actionPrimaryDark : APP_COLORS.stroke.actionPrimary
      : isDarkMode ? APP_COLORS.stroke.primaryDark : APP_COLORS.stroke.primary,
    fontWeight: "400",
    backgroundColor: isDarkMode ? APP_COLORS.background.primaryDark : "#fff",
    color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
    fontSize: "14px",
    boxShadow: state.menuIsOpen ? "0px 0px 0px 3px #B9D6D1" : "none",
    "&:hover": {
      borderColor: isDarkMode ? APP_COLORS.stroke.secondaryDark : APP_COLORS.stroke.secondary,
    },
  }),
  menu: (provided) => ({
    ...provided,
    marginTop: 5,
    backgroundColor: isDarkMode ? APP_COLORS.background.primaryDark : "#fff",
    borderColor: isDarkMode ? APP_COLORS.stroke.primaryDark : "#E5E7EB",
    borderRadius: "0.5rem",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: menuMaxHeight,
    overflowY: "auto",
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: isDarkMode ? APP_COLORS.background.primaryDark : "#fff",
  }),
  option: () => ({
    backgroundColor: "transparent",
    cursor: "pointer",
  }),
  group: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  groupHeading: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: APP_COLORS.gray["400"],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    "& input": {
      opacity: 0,
      width: 0,
      height: 0,
    },
  }),
  multiValue: () => ({
    display: "inline-flex",
    margin: 0,
  }),
  multiValueLabel: () => ({
    padding: 0,
  }),
  multiValueRemove: () => ({
    display: "none",
  }),
  valueContainer: (provided) => ({
    ...provided,
    flexWrap: "wrap" as const,
    gap: "4px",
    padding: "2px 8px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
});

export function SourcePeopleSelect({
  value,
  onChange,
  placeholder,
  companyId: _companyId,
}: ISourcePeopleSelectProps) {
  const { person = [], user = [] } = value;
  const { isDarkMode } = useSystemStore();
  const { groupedOptions, debouncedSearch, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    usePersonAndUserOptions(_companyId);

  const [menuMaxHeight, setMenuMaxHeight] = useState(300);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<SelectInstance<PersonOption, true, GroupBase<PersonOption>>>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchValueRef = useRef("");

  // Stable refs for values used in component callbacks — prevents selectComponents
  // from being recreated on every selection, which would remount MenuList and reset scroll.
  const personRef = useRef(person);
  const userRef = useRef(user);
  const selectedCountRef = useRef(0);
  personRef.current = person;
  userRef.current = user;

  const calculateMenuMaxHeight = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const offset = 50;
      const spaceBelow = window.innerHeight - rect.bottom - offset;
      setMenuMaxHeight(Math.min(Math.max(spaceBelow, 150), 400));
    }
  }, []);

  const handleMenuOpen = useCallback(() => {
    setMenuIsOpen(true);
    calculateMenuMaxHeight();
    window.addEventListener("resize", calculateMenuMaxHeight);
    window.addEventListener("scroll", calculateMenuMaxHeight, true);
  }, [calculateMenuMaxHeight]);

  const closeMenu = useCallback(() => {
    setMenuIsOpen(false);
    searchValueRef.current = "";
    if (searchInputRef.current) searchInputRef.current.value = "";
    debouncedSearch("");
    window.removeEventListener("resize", calculateMenuMaxHeight);
    window.removeEventListener("scroll", calculateMenuMaxHeight, true);
  }, [calculateMenuMaxHeight, debouncedSearch]);

  // Handle click outside to close menu
  useEffect(() => {
    if (!menuIsOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the container
      if (containerRef.current?.contains(target)) return;

      // Check if click is inside the portal menu
      const menuPortal = document.querySelector(".select__menu-portal");
      if (menuPortal?.contains(target)) return;

      closeMenu();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuIsOpen, closeMenu]);

  const createPersonOption = useCallback((item: PersonValueItem): PersonOption => {
    const value = getItemId(item);
    return {
      id: getItemId(item),
      value,
      label: getItemLabel(item),
      email: item.email,
      first_name: item.first_name,
      last_name: item.last_name,
      avatar_url: item.avatar_url,
      logo_url: item.logo_url || item.primary_company?.company?.logo_url,
      primary_company: item.primary_company,
      isTeamMember: false,
    };
  }, []);

  const createUserOption = useCallback((item: PersonValueItem): PersonOption => {
    const value = getItemId(item);
    return {
      id: getItemId(item),
      value,
      label: getItemLabel(item),
      email: item.email,
      first_name: item.first_name,
      last_name: item.last_name,
      avatar_url: item.avatar_url,
      isTeamMember: true,
      isUser: true,
      title: item.title,
    };
  }, []);

  // Convert selected items to react-select value format
  const selectedOptions = useMemo(() => {
    const personOptions = person.map(createPersonOption);
    const userOptions = user.map(createUserOption);
    return [...personOptions, ...userOptions];
  }, [person, user, createPersonOption, createUserOption]);

  // Merge options with any selected items that might not be in the fetched options
  const mergedOptions = useMemo(() => {
    if (!groupedOptions) return [];

    const existingValues = new Set<string>();
    groupedOptions.forEach((group: any) => {
      group.options?.forEach((opt: any) => {
        const value: string = typeof opt.value === "string" ? opt.value : String(opt.value ?? "");
        if (value) existingValues.add(value);
      });
    });

    const missingPersonItems = person.filter((item) => {
      const itemValue = getItemId(item);
      const itemId = getItemId(item);
      return !existingValues.has(itemValue) && !existingValues.has(itemId);
    });
    const missingUserItems = user.filter((item) => {
      const itemValue = getItemId(item);
      const itemId = getItemId(item);
      return !existingValues.has(itemValue) && !existingValues.has(itemId);
    });

    if (missingPersonItems.length === 0 && missingUserItems.length === 0) {
      return groupedOptions;
    }

    const placedPersonIds = new Set<string>();
    const newGroups = groupedOptions.map((group: any) => {
      if ((group.label === "PEOPLE" || group.label === "ADDITIONAL") && missingPersonItems.length > 0) {
        const existingVals = new Set(group.options.map((opt: any) => opt.value));
        const newMissing = missingPersonItems
          .filter((item) => {
            const id = getItemId(item);
            return !existingVals.has(id) && !placedPersonIds.has(id);
          })
          .map(createPersonOption);
        newMissing.forEach((opt) => placedPersonIds.add(opt.value));
        return { ...group, options: [...group.options, ...newMissing] };
      }
      if (group.label === "TEAM MEMBERS" && missingUserItems.length > 0) {
        const existingVals = new Set(group.options.map((opt: any) => opt.value));
        const newMissing = missingUserItems
          .filter((item) => !existingVals.has(getItemId(item)))
          .map(createUserOption);
        return { ...group, options: [...group.options, ...newMissing] };
      }
      return group;
    });

    return newGroups;
  }, [groupedOptions, person, user, createPersonOption, createUserOption]);

  const handleChange = (newValue: readonly PersonOption[]) => {
    const persons: PersonValueItem[] = [];
    const users: PersonValueItem[] = [];

    newValue.forEach((option) => {
      // Check if this was an existing selected item
      const existingPerson = person.find((item) => getItemId(item) === option.value);
      if (existingPerson) {
        persons.push(existingPerson);
        return;
      }

      const existingUser = user.find((item) => getItemId(item) === option.value);
      if (existingUser) {
        users.push(existingUser);
        return;
      }

      // Find the full option data from merged options
      let foundOption: PersonOption | null = null;
      for (const group of mergedOptions) {
        foundOption = (group as any).options?.find((opt: PersonOption) => opt.value === option.value) || null;
        if (foundOption) break;
      }

      if (foundOption) {
        const obj: PersonValueItem = {
          id: option.value,
          label: typeof foundOption.label === "string" ? foundOption.label : String(foundOption.label || ""),
          email: foundOption.email,
          first_name: foundOption.first_name,
          last_name: foundOption.last_name,
          avatar_url: foundOption.avatar_url,
          logo_url: foundOption.logo_url,
          primary_company: foundOption.primary_company || null,
          title: foundOption.title,
        };

        if (foundOption.isTeamMember || foundOption.isUser) {
          users.push(obj);
        } else {
          persons.push(obj);
        }
      }
    });

    onChange({ person: persons, user: users });
  };

  const handleRemove = useCallback((optionToRemove: PersonOption) => {
    const newPeople = personRef.current.filter((item) => getItemId(item) !== optionToRemove.value);
    const newUsers = userRef.current.filter((item) => getItemId(item) !== optionToRemove.value);
    onChange({ person: newPeople, user: newUsers });
  }, [onChange]);

  useEffect(() => {
    return () => {
      window.removeEventListener("resize", calculateMenuMaxHeight);
      window.removeEventListener("scroll", calculateMenuMaxHeight, true);
    };
  }, [calculateMenuMaxHeight]);

  const handleClearAll = useCallback(() => {
    onChange({ person: [], user: [] });
  }, [onChange]);

  selectedCountRef.current = person.length + user.length;

  const selectComponents = useMemo(
    () => ({
      Option: (optionProps: OptionProps<PersonOption, true, GroupBase<PersonOption>>) => (
        <CustomOption optionProps={optionProps} />
      ),
      Control: CustomControl,
      GroupHeading: CustomGroupHeading,
      DropdownIndicator,
      ClearIndicator: () => (
        <CustomClearIndicator clearValue={handleClearAll} hasValue={selectedCountRef.current > 0} />
      ),
      MenuList: ({ children, ...props }: any) => (
        <components.MenuList {...props}>
          <div
            className={cn(
              "sticky top-0 z-20 flex items-center gap-2 border-b px-2 py-1",
              isDarkMode ? "border-gray-700 bg-gray-850" : "border-gray-200 bg-white"
            )}
          >
            <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={(el) => {
                searchInputRef.current = el;
                if (el) {
                  el.value = searchValueRef.current;
                  el.focus();
                }
              }}
              onChange={(e) => {
                searchValueRef.current = e.target.value;
                debouncedSearch(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  closeMenu();
                } else {
                  e.stopPropagation();
                }
              }}
              onMouseDown={(e) => e.stopPropagation()}
              placeholder="Search"
              className={cn(
                "flex-1 border-none bg-transparent text-sm outline-none shadow-none ring-0 focus:border-none focus:outline-none focus:ring-0 focus:shadow-none placeholder:text-gray-400 h-5",
                isDarkMode ? "text-white" : "text-gray-900"
              )}
              style={{ outline: "none", boxShadow: "none", border: "none" }}
            />
          </div>
          {children}
        </components.MenuList>
      ),
      MultiValue: (mvProps: MultiValueProps<PersonOption, true, GroupBase<PersonOption>>) => {
        const data = mvProps.data;
        const isUser = data.isTeamMember || data.isUser;
        const primaryCompany = data.primary_company?.company;
        const avatarUrl = primaryCompany?.logo_url || data.avatar_url || data.logo_url || "";
        const displayName = data.label || data.email || "";

        return (
          <div
            className={cn(
              "inline-flex items-center gap-1.5 py-1 pl-1 pr-2 rounded-lg max-w-full",
              isUser
                ? "bg-teal-100 dark:bg-teal-900"
                : "bg-gray-100 dark:bg-gray-700"
            )}
          >
            <CompanyAvatar
              size={CompanyAvatarSizes.SMALL}
              logoUrl={avatarUrl}
              userProps={{
                first_name: data.first_name ?? "",
                last_name: data.last_name ?? "",
              }}
            />
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {displayName}
            </span>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemove(data);
              }}
              className="flex-shrink-0 cursor-pointer opacity-70 duration-200 hover:opacity-100"
            >
              <CloseRoundedIcon width={12} height={12} color="#6B7080" />
            </button>
          </div>
        );
      },
    }),
    [handleClearAll, handleRemove, isDarkMode, debouncedSearch, closeMenu]
  );

  return (
    <div ref={containerRef}>
      <Select<PersonOption, true, GroupBase<PersonOption>>
        ref={selectRef}
        value={selectedOptions}
        isMulti
        classNamePrefix="select"
        menuIsOpen={menuIsOpen}
        openMenuOnFocus
        controlShouldRenderValue
        hideSelectedOptions={false}
        isSearchable
        closeMenuOnSelect={false}
        components={selectComponents}
        isClearable={false}
        menuPlacement="auto"
        menuPortalTarget={document.body}
        filterOption={null}
        styles={getStyles(isDarkMode, menuMaxHeight)}
        options={mergedOptions as GroupBase<PersonOption>[]}
        isLoading={isLoading}
        placeholder={placeholder}
        onChange={handleChange}
        onMenuOpen={handleMenuOpen}
        onMenuClose={() => {}}
        onMenuScrollToBottom={() => {
          if (hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
          }
        }}
      />
    </div>
  );
}
